/**
 * The shared menu's model and controller (ADR 0194, decisions 2, 3 and 5):
 * the popup's Dismiss ▾ and More ⋯, a card's right-click menu, and the
 * selection bar's Dismiss all open the same menu. `Menu.tsx` draws it.
 *
 * **A button's menu** (Dismiss ▾, More ⋯, the selection bar's Dismiss):
 * `MenuButton`, which opens it below the button and keeps `aria-expanded`:
 *
 *     <MenuButton menuLabel="Dismiss as" items={() => dismissItems(block)}>
 *       Dismiss <span aria-hidden="true">▾</span>
 *     </MenuButton>
 *
 *     <MenuButton
 *       menuLabel="Dismiss as"
 *       items={() => bulkDismissItems(selection)}
 *       clearOf={(button) => button.closest("[data-part='selection-bar']")}
 *     >
 *       Dismiss <span aria-hidden="true">▾</span>
 *     </MenuButton>
 *
 * **A menu at a point** (the right-click menu): `useMenu()`, with `<Menu>`
 * rendered beside what opens it:
 *
 *     const menu = useMenu();
 *     <div
 *       onContextMenu={(event) => {
 *         event.preventDefault();
 *         menu.openAt(event.clientX, event.clientY, cardItems(card), {
 *           label: `${client}, ${times}`,
 *           header: { title: client, line: `${times} · ${duration}` },
 *           opener: cardButton,
 *         });
 *       }}
 *       onKeyDown={(event) => {
 *         if (isContextMenuKey(event)) {
 *           event.preventDefault();
 *           const box = event.currentTarget.getBoundingClientRect();
 *           menu.openAt(box.left, box.top, cardItems(card), { … });
 *         }
 *       }}
 *     />
 *     <Menu control={menu} />
 *
 * Where to render `<Menu>`: React passes a portal's events up its own tree,
 * so render it in the component that opens it. Opened from inside a dialog,
 * it then counts as inside that dialog (its focus trap, Escape, and a click
 * outside it), and goes on the page inside the dialog's element.
 *
 * Only one menu is open at a time: opening one closes any other.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface MenuItem {
  /** What it says. A letter typed in the menu jumps to the next item starting with it. */
  label: string;
  /** An icon from Icons.tsx, before the label. */
  icon?: ReactNode;
  /** Muted, after the label: a key ("Enter") or a duration ("33m"). Not read out. */
  hint?: string | undefined;
  /** In the amber of Dismiss and its reasons. */
  warn?: boolean | undefined;
  /** Greyed: the arrow keys pass it, and it can't be chosen. */
  disabled?: boolean | undefined;
  /** Why it's disabled ("different clients"): shown as its hint, and read as its description. */
  reason?: string | undefined;
  /** Its name for a screen reader, when the hint says something the label doesn't. */
  ariaLabel?: string | undefined;
  /** Chosen. The menu has closed, and focus is back on what opened it. */
  onSelect?: ((how: { keyboard: boolean }) => void) | undefined;
  /** A submenu, built when it opens. */
  items?: (() => readonly MenuEntry[]) | undefined;
}

export interface MenuSeparator {
  readonly separator: true;
}

/** A rule between groups of items. Leading, trailing and doubled rules are dropped. */
export const MENU_SEPARATOR: MenuSeparator = { separator: true };

export type MenuEntry = MenuItem | MenuSeparator;

/** A small muted title and line over the items, such as a card's client and times. */
export interface MenuHeader {
  title: ReactNode;
  line?: ReactNode;
}

export interface MenuAtOptions {
  /** The menu's name for a screen reader (the header isn't read). */
  label?: string | undefined;
  header?: MenuHeader | undefined;
  /** Where focus goes back to. By default, whatever had it when the menu opened. */
  opener?: HTMLElement | null | undefined;
}

export interface MenuFromOptions {
  /** The menu's name; by default it's named by the button. */
  label?: string | undefined;
  header?: MenuHeader | undefined;
  /** Opening above the button, the menu clears this element's top instead (a bar it's in). */
  clearOf?: Element | null | undefined;
}

/** What's open, for `<Menu>`. */
export interface MenuRequest {
  readonly serial: number;
  readonly items: readonly MenuEntry[];
  readonly label: string | undefined;
  readonly labelledBy: string | undefined;
  readonly header: MenuHeader | undefined;
  readonly point: { readonly x: number; readonly y: number } | null;
  readonly anchor: HTMLElement | null;
  readonly clearOf: Element | null;
  readonly opener: HTMLElement | null;
}

export interface MenuControl {
  readonly isOpen: boolean;
  /** The button the menu hangs from, while it's open from one. */
  readonly anchor: HTMLElement | null;
  /** Opens it at a point (a right-click), flipping left or up near the window's edges. */
  readonly openAt: (x: number, y: number, items: readonly MenuEntry[], options?: MenuAtOptions) => void;
  /**
   * Opens it below a button, or above when there's no room. Called again
   * from the same button, it closes. Give the button `aria-haspopup="menu"`
   * and `aria-expanded={menu.isOpen}`, or use `MenuButton`, which does.
   */
  readonly openFrom: (button: HTMLElement, items: readonly MenuEntry[], options?: MenuFromOptions) => void;
  /** Closes it; focus in it goes back to what opened it. */
  readonly close: () => void;
  /** For `<Menu>`: what's open. */
  readonly request: MenuRequest | null;
  /** For `<Menu>`: closes it, moving focus back to what opened it or not. */
  readonly dismiss: (restoreFocus: boolean) => void;
}

/** ☰ or Shift+F10: a focused card's menu (ADR 0194, decision 3). */
export function isContextMenuKey(event: { key: string; shiftKey: boolean }): boolean {
  return event.key === "ContextMenu" || (event.key === "F10" && event.shiftKey);
}

// Presses that closed a menu, so a dialog can stay open for them.
const dismissPresses = new WeakSet<Event>();

/** Marks a press that closed a menu (`Menu`). */
export function markDismissPress(event: Event): void {
  dismissPresses.add(event);
}

/**
 * Whether this press closed a menu. In the prototype a press on the popup's
 * backdrop with one of its menus open only closes the menu; a dialog gets
 * that with `onPointerDownOutside={(e) => { if (isMenuDismissPress(e.detail.originalEvent)) e.preventDefault(); }}`.
 */
export function isMenuDismissPress(event: Event): boolean {
  return dismissPresses.has(event);
}

/** Marks every menu panel on the page. */
export const MENU_PANEL = "data-ui-menu";

function focusInMenu(): boolean {
  return document.activeElement?.closest(`[${MENU_PANEL}]`) != null;
}

function focusedElement(): HTMLElement | null {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body ? active : null;
}

// The open menu, so that opening another closes it.
let openMenu: { owner: object; dismiss: (restoreFocus: boolean) => void } | null = null;
let serials = 0;

export function useMenu(): MenuControl {
  const [request, setRequest] = useState<MenuRequest | null>(null);
  // The same, for the handlers that run before a render.
  const current = useRef<MenuRequest | null>(null);

  const dismiss = useCallback((restoreFocus: boolean) => {
    const was = current.current;
    if (was === null) {
      return;
    }
    current.current = null;
    setRequest(null);
    if (openMenu?.owner === current) {
      openMenu = null;
    }
    if (restoreFocus && was.opener?.isConnected) {
      was.opener.focus({ preventScroll: true });
    }
  }, []);

  const show = useCallback(
    (next: Omit<MenuRequest, "serial">) => {
      if (openMenu !== null && openMenu.owner !== current) {
        openMenu.dismiss(false);
      }
      openMenu = { owner: current, dismiss };
      serials += 1;
      const opened = { ...next, serial: serials };
      current.current = opened;
      setRequest(opened);
    },
    [dismiss],
  );

  const openAt = useCallback(
    (x: number, y: number, items: readonly MenuEntry[], options: MenuAtOptions = {}) => {
      show({
        items,
        label: options.label,
        labelledBy: undefined,
        header: options.header,
        point: { x, y },
        anchor: null,
        clearOf: null,
        opener: options.opener === undefined ? focusedElement() : options.opener,
      });
    },
    [show],
  );

  const openFrom = useCallback(
    (button: HTMLElement, items: readonly MenuEntry[], options: MenuFromOptions = {}) => {
      if (current.current?.anchor === button) {
        dismiss(true);
        return;
      }
      show({
        items,
        label: options.label,
        labelledBy: options.label === undefined && button.id !== "" ? button.id : undefined,
        header: options.header,
        point: null,
        anchor: button,
        clearOf: options.clearOf ?? null,
        opener: button,
      });
    },
    [dismiss, show],
  );

  const close = useCallback(() => {
    dismiss(focusInMenu());
  }, [dismiss]);

  // A menu whose owner goes leaves nothing behind.
  useEffect(
    () => () => {
      if (openMenu?.owner === current) {
        openMenu = null;
      }
    },
    [],
  );

  return useMemo(
    () => ({
      isOpen: request !== null,
      anchor: request?.anchor ?? null,
      openAt,
      openFrom,
      close,
      request,
      dismiss,
    }),
    [request, openAt, openFrom, close, dismiss],
  );
}
